/**
 * The Paperwork row's second line — where a file came from, how big it is, and
 * who put it there (#1538).
 *
 * ── Why a file needs to introduce itself ──────────────────────────────────
 *
 * Corbin, 2026-09-16, job 092678249: two report rows carrying the identical
 * name and the identical date. One was the blank generated copy, one was the
 * real uploaded record, and the screen gave him no way to tell which was which.
 * A rename or a delete button on rows like that is a coin flip — so this line
 * lands FIRST and the buttons hang off it.
 *
 * Size does the heavy lifting: a blank form and a filled one are never the same
 * number of kilobytes. 31 KB against 412 KB answered that job on sight.
 *
 * Every value here is already stored on the row (`file_size`, `uploaded_by`,
 * `created_at`, `category`) — this is a read, not new plumbing. Counting how
 * many fields on a PDF are actually filled would be the stronger signal, and is
 * deliberately NOT here: it needs the bytes parsed, and it is the same question
 * Issue #458 is already chasing. Corbin's call, 2026-09-16: size + source now.
 */

/** Categories whose PDF Groundwork builds itself. Mirrors the server's
 *  `GENERATED_CATEGORIES` (services/attachmentRename.ts) — kept in step by
 *  `paperwork-provenance.test.ts`. */
const GENERATED_CATEGORIES = new Set([
    "test_report",
    "test_report_city",
    "test_report_paper",
    "report",
    "summary",
    "work_order_filled",
]);

export function isGeneratedCategory(category: string | null | undefined): boolean {
    return !!category && GENERATED_CATEGORIES.has(category);
}

/**
 * Whether the ✎ is offered on a row. Mirrors the server's
 * `isRenameableCategory` — the server is the gate, this only decides whether
 * we show a button that would be refused.
 *
 * Generated files are excluded because a regenerate re-derives the name from
 * the record and would silently discard the rename (Corbin's call, 2026-09-16).
 */
export function isRenameableCategory(category: string | null | undefined): boolean {
    if (!category || category === "signature") return false;
    return !GENERATED_CATEGORIES.has(category);
}

/**
 * "Generated by Groundwork" / "Uploaded by Darcy Alvarez" / "Uploaded".
 *
 * Source is read off the CATEGORY, not off whether `uploaded_by` happens to be
 * set — a generated report still carries the submitting tech's id, so guessing
 * from the column would label our own PDF as that tech's upload.
 */
export function sourceLabel(a: { category?: string | null; uploaded_by_name?: string | null }): string {
    if (isGeneratedCategory(a.category)) return "Generated by Groundwork";
    const who = (a.uploaded_by_name ?? "").trim();
    return who ? `Uploaded by ${who}` : "Uploaded";
}

/**
 * Bytes as something a person reads. Deliberately coarse — the office is
 * comparing two files, not auditing storage, and "31 KB" next to "412 KB"
 * makes the point that "31,204 bytes" does not.
 */
export function formatFileSize(bytes: number | null | undefined): string | null {
    if (bytes === null || bytes === undefined) return null;
    if (!Number.isFinite(bytes) || bytes < 0) return null;
    if (bytes === 0) return "0 KB";
    if (bytes < 1024) return "1 KB"; // never "0 KB" for a file that has bytes
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    const mb = kb / 1024;
    return `${mb >= 10 ? Math.round(mb) : Math.round(mb * 10) / 10} MB`;
}

/** "Sep 16, 11:41 AM" — same day-and-clock shape the Activity feed uses. */
export function formatWhen(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

/**
 * The whole line, already joined: source · when · size. Pieces that aren't
 * known are left out rather than printed as "unknown" — a row that says
 * "Uploaded · Sep 16" is honest; one that says "— · — · 0 KB" is noise.
 */
export function provenanceLine(a: {
    category?: string | null;
    uploaded_by_name?: string | null;
    created_at?: string | null;
    file_size?: number | null;
}): string {
    return [sourceLabel(a), formatWhen(a.created_at), formatFileSize(a.file_size)].filter(Boolean).join(" · ");
}
